import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type {
  LoginRequest,
  AuthResponse,
  User,
  Deceased,
  UserDto,
  CreateUserDto,
  UpdateUserDto,
  SetInitialPasswordRequest,
  VisitEventType,
  VisitTrendGranularity,
  VisitTrendResponse,
  VisitDistributionResponse,
  VisitTopDeceasedResponse,
  CemeterySummary,
  CreateCemeteryRequest
  ,
  UpdateCemeteryRequest
} from '../types';
import { createStore } from 'devextreme-aspnet-data-nojquery';

// Configure axios defaults
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';

console.log('API Base URL configured as:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookie-based authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // If sending FormData, delete Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Redirect to login or handle unauthorized access
      window.location.href = '/admin/login';
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  checkAuth: async (): Promise<{ isAuthenticated: boolean; user?: User }> => {
    const response = await api.get('/auth/check');
    return response.data;
  },

  checkPasswordStatus: async (email: string): Promise<{ requiresPasswordSetup: boolean; user?: User }> => {
    const response = await api.post('/auth/check-password-status', { email });
    return response.data;
  },

  setInitialPassword: async (request: SetInitialPasswordRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/set-initial-password', request);
    return response.data;
  },
};

export const deceasedApi = {
  // DevExtreme Data Store for server-side processing
  createStore: () => createStore({
    key: 'Id',
    loadUrl: `${API_BASE_URL}/deceased`,
    insertUrl: `${API_BASE_URL}/deceased`,
    updateUrl: `${API_BASE_URL}/deceased`,
    deleteUrl: `${API_BASE_URL}/deceased`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
    },
  }),

  getAll: async (): Promise<Deceased[]> => {
    const response: AxiosResponse<Deceased[]> = await api.get('/deceased');
    return response.data;
  },

  getById: async (id: number): Promise<Deceased> => {
    const response: AxiosResponse<Deceased> = await api.get(`/deceased/${id}`);
    return response.data;
  },

  create: async (deceased: Partial<Deceased>): Promise<Deceased> => {
    const response: AxiosResponse<Deceased> = await api.post('/deceased', deceased);
    return response.data;
  },

  update: async (id: number, deceased: Partial<Deceased>): Promise<void> => {
    await api.put(`/deceased/${id}`, deceased);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/deceased/${id}`);
  },

  // Autocomplete data sources
  getFirstNames: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/firstnames', { params: { cemeterySlug } });
    return response.data;
  },

  getLastNames: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/lastnames', { params: { cemeterySlug } });
    return response.data;
  },

  getMiddleNames: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/middlenames', { params: { cemeterySlug } });
    return response.data;
  },

  getMaidenNames: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/maidennames', { params: { cemeterySlug } });
    return response.data;
  },

  getGraveTypes: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/gravetypes', { params: { cemeterySlug } });
    return response.data;
  },
};

export const usersApi = {
  createStore: () => createStore({
    key: 'id',
    loadUrl: `${API_BASE_URL}/users`,
    insertUrl: `${API_BASE_URL}/users`,
    updateUrl: `${API_BASE_URL}/users`,
    deleteUrl: `${API_BASE_URL}/users`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
    },
  }),

  getById: async (id: string): Promise<UserDto> => {
    const response: AxiosResponse<UserDto> = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (user: CreateUserDto): Promise<UserDto> => {
    const response: AxiosResponse<UserDto> = await api.post('/users', user);
    return response.data;
  },

  update: async (id: string, user: UpdateUserDto): Promise<void> => {
    await api.put(`/users/${id}`, user);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  toggleStatus: async (id: string): Promise<void> => {
    await api.post(`/users/${id}/toggle-status`);
  },

  resetPassword: async (id: string): Promise<void> => {
    await api.post(`/users/${id}/reset-password`);
  },
};

export const searchApi = {
  search: async (params: {
    name?: string;
    birthYear?: number;
    deathYear?: number;
    page?: number;
    pageSize?: number;
  }) => {
    const response = await api.get('/search', { params });
    return response.data;
  },

  getDetails: async (id: number) => {
    const response = await api.get(`/search/${id}/details`);
    return response.data;
  },

  getSuggestions: async (term: string) => {
    const response = await api.get('/search/suggestions', { params: { term } });
    return response.data;
  },
};

export const cemeteryApi = {
  list: async (includeInactive = false): Promise<CemeterySummary[]> => {
    const response: AxiosResponse<CemeterySummary[]> = await api.get('/cemeteries', { params: { includeInactive } });
    return response.data;
  },

  getDefault: async (): Promise<CemeterySummary> => {
    const response: AxiosResponse<CemeterySummary> = await api.get('/cemeteries/default');
    return response.data;
  },

  createCemetery: async (payload: CreateCemeteryRequest): Promise<CemeterySummary> => {
    const response: AxiosResponse<CemeterySummary> = await api.post('/cemeteries', payload);
    return response.data;
  },

  updateCemetery: async (id: number, payload: UpdateCemeteryRequest): Promise<CemeterySummary> => {
    const response: AxiosResponse<CemeterySummary> = await api.put(`/cemeteries/${id}`, payload);
    return response.data;
  },

  getCemeteryData: async (cemeterySlug?: string) => {
    const response = await api.get('/cemetery', { params: { cemeterySlug } });
    return response.data;
  },

  getPublicCemeteryData: async (cemeterySlug?: string) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
    const url = cemeterySlug ? `${baseUrl}/cemetery/public/${encodeURIComponent(cemeterySlug)}` : `${baseUrl}/cemetery/public`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to load cemetery data');
    }

    const text = await response.text();
    if (!text.trim()) {
      return null;
    }

    return JSON.parse(text);
  },

  uploadPhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('cemetery/upload-photo', formData);
    return response.data;
  },

  saveCemeteryData: async (data: {
    photoFileName: string;
    photoWidth: number;
    photoHeight: number;
    overlayTopLeftLat?: number;
    overlayTopLeftLng?: number;
    overlayTopRightLat?: number;
    overlayTopRightLng?: number;
    overlayBottomLeftLat?: number;
    overlayBottomLeftLng?: number;
    overlayBottomRightLat?: number;
    overlayBottomRightLng?: number;
    overlayOpacity?: number;
    referencePoints: Array<{
      name: string;
      iconKey: string;
      description?: string;
      latitude?: number;
      longitude?: number;
      x?: number;
      y?: number;
      showOnPublicMap: boolean;
    }>;
  }, cemeterySlug?: string) => {
    const response = await api.post('/cemetery', data, { params: { cemeterySlug } });
    return response.data;
  },

  updateCemeteryData: async (id: number, data: {
    overlayTopLeftLat?: number;
    overlayTopLeftLng?: number;
    overlayTopRightLat?: number;
    overlayTopRightLng?: number;
    overlayBottomLeftLat?: number;
    overlayBottomLeftLng?: number;
    overlayBottomRightLat?: number;
    overlayBottomRightLng?: number;
    overlayOpacity?: number;
    referencePoints: Array<{
      name: string;
      iconKey: string;
      description?: string;
      latitude?: number;
      longitude?: number;
      x?: number;
      y?: number;
      showOnPublicMap: boolean;
    }>;
  }, cemeterySlug?: string) => {
    const response = await api.put(`/cemetery/${id}`, data, { params: { cemeterySlug } });
    return response.data;
  },

  deleteCemeteryData: async (id: number) => {
    await api.delete(`/cemetery/${id}`);
  },
};

export const graveSitesApi = {
  // DevExtreme Data Store for server-side processing
  createStore: (cemeterySlug?: string) => createStore({
    key: 'id',
    loadUrl: `${API_BASE_URL}/gravesites`,
    insertUrl: `${API_BASE_URL}/gravesites`,
    updateUrl: `${API_BASE_URL}/gravesites`,
    deleteUrl: `${API_BASE_URL}/gravesites`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
      if (cemeterySlug) {
        const requestUrl = ajaxOptions.url ?? `${API_BASE_URL}/gravesites`;
        ajaxOptions.url = `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}cemeterySlug=${encodeURIComponent(cemeterySlug)}`;
      }
    },
  }),

  getAll: async (cemeterySlug?: string) => {
    const response = await api.get('/gravesites', { params: { cemeterySlug } });
    return response.data;
  },

  getById: async (id: number, cemeterySlug?: string) => {
    const response = await api.get(`/gravesites/${id}`, { params: { cemeterySlug } });
    return response.data;
  },

  create: async (graveSite: any, cemeterySlug?: string) => {
    const response = await api.post('/gravesites', graveSite, { params: { cemeterySlug } });
    return response.data;
  },

  update: async (id: number, graveSite: any, cemeterySlug?: string) => {
    const response = await api.put(`/gravesites/${id}`, graveSite, { params: { cemeterySlug } });
    return response.data;
  },

  getCategories: async (cemeterySlug?: string): Promise<string[]> => {
    const response = await api.get('/gravesites/categories', { params: { cemeterySlug } });
    return response.data;
  },

  updateLocation: async (id: number, locationX: number | null, locationY: number | null, cemeterySlug?: string) => {
    const response = await api.put(`/gravesites/${id}/location`, { locationX, locationY }, { params: { cemeterySlug } });
    return response.data;
  },

  updateGeoLocation: async (id: number, latitude: number, longitude: number, cemeterySlug?: string) => {
    const response = await api.put(`/gravesites/${id}/geolocation`, { latitude, longitude }, { params: { cemeterySlug } });
    return response.data;
  },

  uploadPhoto: async (id: number, file: File, cemeterySlug?: string) => {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post(`/gravesites/${id}/photo`, formData, {
      params: { cemeterySlug },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deletePhoto: async (id: number, cemeterySlug?: string) => {
    const response = await api.delete(`/gravesites/${id}/photo`, { params: { cemeterySlug } });
    return response.data;
  },

  getCemeteryPhoto: async (cemeterySlug?: string) => {
    const response = await api.get('/gravesites/cemetery-photo', { params: { cemeterySlug } });
    return response.data;
  },

  exportAll: async (cemeterySlug?: string) => {
    const response = await api.get('/gravesites/export', { params: { cemeterySlug }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.setAttribute('download', `Pokopani_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportSelected: async (ids: number[], cemeterySlug?: string) => {
    const response = await api.post('/gravesites/export', { ids }, { params: { cemeterySlug }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.setAttribute('download', `Pokopani_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getDeceasedByGrave: (graveSiteId: number, cemeterySlug?: string) => createStore({
    key: 'id',
    loadUrl: `${API_BASE_URL}/deceased/by-gravesite/${graveSiteId}`,
    insertUrl: `${API_BASE_URL}/deceased`,
    updateUrl: `${API_BASE_URL}/deceased`,
    deleteUrl: `${API_BASE_URL}/deceased`,
    onBeforeSend: (method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
      if (cemeterySlug) {
        ajaxOptions.data = { ...(ajaxOptions.data || {}), cemeterySlug };
      }
      
      // For INSERT operations, ensure graveSiteId is included
      if (method === 'POST' && ajaxOptions.data) {
        const formData = new FormData();
        
        // Parse existing values if they exist
        let values = {};
        if (typeof ajaxOptions.data === 'string') {
          const params = new URLSearchParams(ajaxOptions.data);
          const valuesParam = params.get('values');
          if (valuesParam) {
            values = JSON.parse(valuesParam);
          }
        }
        
        // Add graveSiteId to the values
        values = { ...values, graveSiteId };
        
        // Rebuild form data
        formData.append('values', JSON.stringify(values));
        ajaxOptions.data = formData;
      }
    },
  }),
};

export const visitsApi = {
  logVisit: async (payload: { eventType: VisitEventType; sessionId: string; deceasedId?: number; cemeterySlug?: string }) => {
    const response = await api.post('/visittracking/log', payload);
    return response.data;
  },

  getTrend: async (params: { startDate: string; endDate: string; granularity: VisitTrendGranularity; cemeterySlugs?: string }): Promise<VisitTrendResponse> => {
    const response: AxiosResponse<VisitTrendResponse> = await api.get('/visitstats/trend', { params });
    return response.data;
  },

  getWeekdayDistribution: async (params: { startDate: string; endDate: string; cemeterySlugs?: string }): Promise<VisitDistributionResponse> => {
    const response: AxiosResponse<VisitDistributionResponse> = await api.get('/visitstats/weekday', { params });
    return response.data;
  },

  getHourlyDistribution: async (params: { startDate: string; endDate: string; cemeterySlugs?: string }): Promise<VisitDistributionResponse> => {
    const response: AxiosResponse<VisitDistributionResponse> = await api.get('/visitstats/hourly', { params });
    return response.data;
  },

  getTopDeceased: async (params: { startDate: string; endDate: string; limit?: number; cemeterySlugs?: string }): Promise<VisitTopDeceasedResponse> => {
    const response: AxiosResponse<VisitTopDeceasedResponse> = await api.get('/visitstats/top-deceased', { params });
    return response.data;
  },
};

export default api;